import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const ConvertTicketToInvoiceModal = ({ isOpen, onClose, ticket, onSuccess }) => {
  const [taxRates, setTaxRates] = useState([]);
  const [selectedTaxRateId, setSelectedTaxRateId] = useState('');
  const [customTaxRate, setCustomTaxRate] = useState('');
  const [useCustomTax, setUseCustomTax] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [timeEntries, setTimeEntries] = useState([]);
  const [includeTimeEntries, setIncludeTimeEntries] = useState(true);
  const [selectedTimeEntries, setSelectedTimeEntries] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchTaxRates();
      fetchTimeEntries();
      const defaultDue = new Date();
      defaultDue?.setDate(defaultDue?.getDate() + 30);
      setDueDate(defaultDue?.toISOString()?.split('T')?.[0]);
    }
  }, [isOpen]);

  const fetchTaxRates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('tax_rates')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('name');
      if (error) throw error;
      const rates = (data || [])?.map(r => ({
        value: r?.id,
        label: `${r?.name} (${r?.rate}%)`,
        rate: r?.rate,
        isDefault: r?.is_default
      }));
      setTaxRates(rates);
      const defaultRate = rates?.find(r => r?.isDefault);
      if (defaultRate) setSelectedTaxRateId(defaultRate?.value);
    } catch (err) {
      console.error('Error fetching tax rates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const { data, error } = await supabase
        ?.from('ticket_time_entries')
        ?.select('*')
        ?.eq('ticket_id', ticket?.id)
        ?.eq('is_billable', true)
        ?.order('created_at');
      if (error) throw error;
      const entries = data || [];
      setTimeEntries(entries);
      // Pre-select all billable time entries
      const selected = {};
      entries?.forEach(e => { selected[e?.id] = true; });
      setSelectedTimeEntries(selected);
    } catch (err) {
      console.error('Error fetching time entries:', err);
    }
  };

  const getEffectiveTaxRate = () => {
    if (useCustomTax) return parseFloat(customTaxRate) || 0;
    const selected = taxRates?.find(r => r?.value === selectedTaxRateId);
    return selected?.rate || 0;
  };

  const formatMinutes = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const getSelectedTimeEntries = () => {
    return includeTimeEntries ? timeEntries?.filter(e => selectedTimeEntries?.[e?.id]) : [];
  };

  const calculateProductsSubtotal = () => {
    return (ticket?.products || [])?.reduce((sum, p) => {
      return sum + (parseFloat(p?.price || 0) * parseInt(p?.quantity || 1));
    }, 0);
  };

  const calculateTimeSubtotal = () => {
    return getSelectedTimeEntries()?.reduce((sum, e) => sum + (parseFloat(e?.billable_amount) || 0), 0);
  };

  const calculateSubtotal = () => calculateProductsSubtotal() + calculateTimeSubtotal();

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const afterDiscount = subtotal - parseFloat(discountAmount || 0);
    return afterDiscount * (getEffectiveTaxRate() / 100);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const afterDiscount = subtotal - parseFloat(discountAmount || 0);
    return afterDiscount + calculateTax();
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0)?.toFixed(2)}`;

  const handleSubmit = async () => {
    if (!dueDate) {
      setError('Please select a due date.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { data: statusData } = await supabase
        ?.from('payment_statuses')
        ?.select('id')
        ?.eq('name', 'Pending')
        ?.single();

      const subtotal = calculateSubtotal();
      const taxRate = getEffectiveTaxRate();
      const taxAmount = calculateTax();
      const totalAmount = calculateTotal();

      const { data: newInvoice, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.insert([{
          customer_id: ticket?.customerId,
          ticket_id: ticket?.id,
          status_id: statusData?.id,
          subtotal,
          tax_rate: taxRate,
          tax_amount: taxAmount,
          discount_amount: parseFloat(discountAmount || 0),
          total_amount: totalAmount,
          amount_due: totalAmount,
          due_date: dueDate,
          notes: notes || `Converted from ticket ${ticket?.ticketId}`
        }])
        ?.select()
        ?.single();

      if (invoiceError) throw invoiceError;

      const invoiceItems = [];

      // Add product line items
      if (ticket?.products?.length > 0) {
        ticket?.products?.forEach(p => {
          invoiceItems?.push({
            invoice_id: newInvoice?.id,
            description: p?.name,
            quantity: p?.quantity || 1,
            unit_price: parseFloat(p?.price || 0),
            discount_percent: 0,
            line_total: parseFloat(p?.price || 0) * parseInt(p?.quantity || 1)
          });
        });
      }

      // Add time entry line items
      const selectedEntries = getSelectedTimeEntries();
      selectedEntries?.forEach(e => {
        const hours = (e?.duration_minutes || 0) / 60;
        invoiceItems?.push({
          invoice_id: newInvoice?.id,
          description: `Labor: ${e?.description || 'Service time'} (${formatMinutes(e?.duration_minutes)})${e?.technician_name ? ' — ' + e?.technician_name : ''}`,
          quantity: parseFloat(hours?.toFixed(2)),
          unit_price: parseFloat(e?.hourly_rate || 0),
          discount_percent: 0,
          line_total: parseFloat(e?.billable_amount || 0)
        });
      });

      if (invoiceItems?.length > 0) {
        const { error: itemsError } = await supabase?.from('invoice_items')?.insert(invoiceItems);
        if (itemsError) throw itemsError;
      }

      await supabase?.from('tickets')?.update({ converted_to_invoice: true, invoice_id: newInvoice?.id })?.eq('id', ticket?.id);
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('category', 'invoice')?.ilike('message', `%${ticket?.ticketId}%`);

      onSuccess?.(newInvoice);
      onClose();
    } catch (err) {
      console.error('Error converting ticket to invoice:', err);
      setError(err?.message || 'Failed to create invoice. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const subtotal = calculateSubtotal();
  const taxRate = getEffectiveTaxRate();
  const taxAmount = calculateTax();
  const total = calculateTotal();
  const selectedEntries = getSelectedTimeEntries();

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="FileCheck" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Convert to Invoice</h2>
              <p className="text-sm text-muted-foreground">Ticket {ticket?.ticketId} — {ticket?.customer}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Ticket Products */}
          {ticket?.products?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Package" size={16} color="var(--color-primary)" />
                Products & Services
              </h3>
              <div className="bg-muted/30 rounded-lg border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Item</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Qty</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Price</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket?.products?.map((p, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-4 py-2 text-sm text-foreground">{p?.name}</td>
                        <td className="px-4 py-2 text-sm text-foreground text-right">{p?.quantity || 1}</td>
                        <td className="px-4 py-2 text-sm text-foreground text-right">{formatCurrency(p?.price)}</td>
                        <td className="px-4 py-2 text-sm font-medium text-foreground text-right">
                          {formatCurrency(parseFloat(p?.price || 0) * parseInt(p?.quantity || 1))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Time Entries Section */}
          {timeEntries?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="Clock" size={16} color="var(--color-primary)" />
                  Billable Time Entries
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTimeEntries}
                    onChange={(e) => setIncludeTimeEntries(e?.target?.checked)}
                    className="w-4 h-4 rounded border-border text-primary"
                  />
                  <span className="text-xs text-muted-foreground">Include in invoice</span>
                </label>
              </div>
              {includeTimeEntries && (
                <div className="bg-muted/30 rounded-lg border border-border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-8"></th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Time</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Rate</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntries?.map((entry) => (
                        <tr key={entry?.id} className="border-t border-border">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedTimeEntries?.[entry?.id] || false}
                              onChange={(e) => setSelectedTimeEntries(prev => ({ ...prev, [entry?.id]: e?.target?.checked }))}
                              className="w-4 h-4 rounded border-border text-primary"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <p className="text-sm text-foreground">{entry?.description || 'Service time'}</p>
                            {entry?.technician_name && <p className="text-xs text-muted-foreground">{entry?.technician_name}</p>}
                          </td>
                          <td className="px-3 py-2 text-sm text-foreground text-right">{formatMinutes(entry?.duration_minutes)}</td>
                          <td className="px-3 py-2 text-sm text-foreground text-right">
                            {entry?.hourly_rate > 0 ? `$${parseFloat(entry?.hourly_rate)?.toFixed(2)}/hr` : '—'}
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-foreground text-right">
                            {formatCurrency(entry?.billable_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {ticket?.products?.length === 0 && timeEntries?.length === 0 && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={18} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">This ticket has no products or time entries. The invoice will be created with $0 subtotal.</p>
            </div>
          )}

          {/* Tax & Discount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Tax Rate</label>
              {!useCustomTax ? (
                <Select
                  placeholder="Select tax rate"
                  options={taxRates}
                  value={selectedTaxRateId}
                  onChange={setSelectedTaxRateId}
                  loading={loading}
                />
              ) : (
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Custom rate %"
                  value={customTaxRate}
                  onChange={(e) => setCustomTaxRate(e?.target?.value)}
                />
              )}
              <button
                onClick={() => setUseCustomTax(!useCustomTax)}
                className="mt-1 text-xs text-primary hover:underline"
              >
                {useCustomTax ? 'Use preset rate' : 'Enter custom rate'}
              </button>
            </div>
            <Input
              label="Discount Amount ($)"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e?.target?.value)}
            />
          </div>

          <Input
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
            <textarea
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              rows={3}
              placeholder="Additional notes for the invoice..."
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
            />
          </div>

          {/* Totals */}
          <div className="bg-muted/30 rounded-lg border border-border p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Products subtotal</span>
              <span className="font-medium text-foreground">{formatCurrency(calculateProductsSubtotal())}</span>
            </div>
            {includeTimeEntries && selectedEntries?.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Labor ({selectedEntries?.length} {selectedEntries?.length === 1 ? 'entry' : 'entries'})</span>
                <span className="font-medium text-foreground">{formatCurrency(calculateTimeSubtotal())}</span>
              </div>
            )}
            {parseFloat(discountAmount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Discount</span>
                <span className="font-medium text-error">-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax ({taxRate}%)</span>
              <span className="font-medium text-foreground">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-border pt-2">
              <span className="text-foreground">Total</span>
              <span className="text-foreground">{formatCurrency(total)}</span>
            </div>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              loading={submitting}
              iconName="FileCheck"
              iconPosition="left"
              className="flex-1 bg-success hover:bg-success/90 text-white"
            >
              Create Invoice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertTicketToInvoiceModal;
